import { useState, useEffect, useContext } from 'react';
import ArtworkCanvas from '../ArtworkCanvas';
import { ColorProvider, ColorContext } from '../Context/ColorContext';
import { BackendContext } from '../Context/BackendContext';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Chat from '../Components/Chat.js';

function ArtParcel() {
  const { pageId, artworkId } = useParams();
  const page = pageId || "null";

  const location = useLocation();
  const navigate = useNavigate();
  const baseRoute = location.pathname.split('/')[1]; // will be "Canvas" or "Events"  

  useEffect(() => {
    if (baseRoute === "Canvas") {
      //console.log("Viewing a canvas page");
    } else if (baseRoute === "Events") {
      //page = "Event_" + pageId; // Adjust page for event context
      //console.log("Viewing an event page");
    }
  }, [baseRoute]);

  //Helpers to keep URL in sync when opening/closing artwork modals
  const handleOpenArtwork = (id) => {
    if (!id) return;
    navigate(`/Canvas/${page}/${id}`, { replace: false });
  };

  const handleClearArtwork = () => {
    navigate(`/Canvas/${page}`, { replace: false });
  }

  return (
    <div className="">
      <ColorProvider>
        <Content 
          page={page}
          deepLinkedArtworkId={artworkId || null}
          onOpenArtwork={handleOpenArtwork}
          onClearArtwork={handleClearArtwork}  
        />
      </ColorProvider>
    </div>
  );
}

function Content({ page, deepLinkedArtworkId, onOpenArtwork, onClearArtwork }) {
  const backendContext = useContext(BackendContext);
  const colorContext = useContext(ColorContext);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const [isBanned, setIsBanned] = useState(false);
  const [banReasons, setBanReasons] = useState([]);

  useEffect(() => {
    async function fetchCanvas() {
      const result = await backendContext.isTextBanned(page, "page_name");
      setIsBanned(result.banned);
      setBanReasons(result.reasons);
      if (result.banned) 
      {
        setIsLoading(false);
        return;
      }
      //console.log("Fetching canvas for page: " + page);
      await backendContext.fetchCanvasS3(page);
      //console.log("Fetching tiles for page: " + page);
      //await backendContext.fetchTiles(page);
      setIsLoading(false);
    }
    fetchCanvas();
  }, [page]);

  // Validate deep-linked artwork id once backend has clients/data:
  useEffect(() => {
    if (!deepLinkedArtworkId || isLoading) return;
    let alive = true;

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    (async () => {
      const checkOnce = async () => {
        try {
          return await backendContext.fetchArtwork(deepLinkedArtworkId);
        } catch {
          return null;
        }
      };

      // Try once, then retry quickly to avoid false negatives during hydration
      let art = await checkOnce();
      if (!art) {
        await sleep(400);
        art = await checkOnce();
      }
      if (!alive) return;

      if (!art) {
        // ❌ Invalid id → clear the id segment (stay on current page)
        onClearArtwork?.();
        // If you prefer jumping to Canvas A instead, replace with:
        // navigate('/Canvas/A', { replace: true });
        return;
      }

      // If found but page mismatches, go to the correct page keeping the id
      const artPage = (art.page ?? '').toLowerCase();
      const curPage = (page ?? '').toLowerCase();
      if (artPage && artPage !== curPage) {
        navigate(`/Canvas/${art.page}/${deepLinkedArtworkId}`, { replace: true });
      }
    })();

    return () => { alive = false; };
  }, [deepLinkedArtworkId, page, isLoading]);

  if(isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-900 text-white">
        Checking page...
      </div>
    )
  }

  if (isBanned) {
    // Nice, minimal block UI (no backend calls, no chat on banned pages)
    return (
      <div className="h-screen w-screen overflow-hidden bg-slate-900 flex items-center justify-center">
        <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-2xl p-6 text-white text-center shadow-xl">
          <h2 className="text-xl font-semibold mb-2">This page is unavailable</h2>
          <p className="text-sm text-slate-300">
            The requested page <span className="font-mono">{page}</span> is not accessible.
          </p>
          <div className="mt-4">
            <a
              href="/Canvas/A"
              className="inline-block px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500"
            >
              Go to Canvas A
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-900">
      <Chat currentPage={page}/>
      <div className="pointer-events-none fixed top-[78px] left-1/2 z-[950] -translate-x-1/2">
        <div className="rounded-full border border-slate-700 bg-slate-950/90 px-5 py-2 text-sm font-semibold tracking-[0.2em] text-white shadow-xl backdrop-blur">
          {page}
        </div>
      </div>
      <div className="page-price pt-16 flex w-full">
        <div className="absolute top-[32px] right-0 bottom-0 left-0 flex flex-col">
          <ArtworkCanvas
            key={page}
            colorContext={colorContext}
            selectedColor={colorContext.selectedColor}
            setSelectedColor={colorContext.setSelectedColor}
            backendContext={backendContext}
            page={page}
            selectedArtworkId={deepLinkedArtworkId}
            onOpenArtwork={onOpenArtwork}
            onClearArtwork={onClearArtwork}
          />
        </div>
        <div className="absolute top-12 left-1 text-white px-3 py-1 rounded text-sm font-mono z-[1000]" />
      </div>
    </div>
  );
}

export default ArtParcel;


